/**
 * Central status dictionary — single source of truth for case status display.
 *
 * Internal status keys stay unchanged (no DB migration, no API break). This
 * module only controls how each status is RENDERED across the app:
 *   - admin Cases page
 *   - planner workspace
 *   - QA workspace
 *   - doctor portal
 *   - production queue
 *
 * Each entry produces:
 *   label         — default human display name (e.g. "Needs Routing")
 *   labels        — optional per-portal overrides (doctor / patient / admin / coe)
 *   tone          — colour family for pills (slate | amber | violet | rose | emerald | sky)
 *   group         — coarse bucket for filtering ("intake" | "planning" | "qa" | "doctor_review"
 *                                                | "approval_routing" | "production" | "treatment" | "terminal")
 *   description   — short tooltip describing what the status means
 *
 * The grouping powers the admin Cases page's high-level tabs without having
 * to know every fine-grained status.
 *
 * Portal-specific labels follow the Phase 1 spec:
 *   - submitted        → "Case Submitted" (doctor) | "Needs Routing" (admin)
 *   - info_requested   → "Doctor Input Needed" (doctor) | "Awaiting Doctor" (admin)
 *   - submitted_to_qa  → "In Planning" (doctor — QA is an internal parallel
 *                        step, not part of the doctor-visible lifecycle)
 */

/* iter101.0 — Doctor-facing labels standardised across the portal.
 * The user reported the same case reading as four different strings
 * across Dashboard / Aligner Cases / Case Detail / Patients. Every
 * doctor-visible "back-office" state (routing → planner → QA →
 * self-plan admin/QA review) now reads "Planning in Progress"; the
 * doctor doesn't need to know which internal desk holds the plan.
 * Only real state changes that require them to act (Plan Ready,
 * Approved, Shipped, In Treatment, …) get their own label.
 */
export const STATUS_CONFIG = {
    draft:                    { label: "Draft",                     tone: "slate",   group: "intake",            description: "Doctor is still preparing the case" },
    // iter97.83 — Doctor chose Invisique's IO Scanning Service at case
    // creation. The case is parked here until the technician completes
    // the visit and the scans are uploaded. From the doctor's view it's
    // a pre-intake state with a single "wait for the visit" message;
    // from the admin's view it's a scheduling signal.
    scan_visit_scheduled:     {
        label: "Scan Visit Scheduled",
        labels: { doctor: "Scan Visit Scheduled", patient: "Scan Visit Scheduled" },
        tone: "sky",     group: "intake",
        description: "Awaiting Invisique technician scan visit before the case can be submitted",
    },
    scan_visit_completed:     {
        label: "Scans Uploaded",
        labels: { doctor: "Scans Uploaded", patient: "Scans Uploaded" },
        tone: "amber",   group: "intake",
        description: "Scan visit complete — doctor needs to resume the case wizard",
    },
    submitted:                {
        label: "Needs Routing",
        labels: { doctor: "Case Submitted", patient: "Case Submitted" },
        tone: "amber",   group: "intake",
        description: "Submitted by doctor — waiting for admin to route to a planner",
    },
    assigned_to_planner:      {
        label: "Awaiting Acceptance",
        labels: { doctor: "Planning in Progress", patient: "Planning in Progress" },
        tone: "sky",     group: "planning",
        description: "Admin routed the case — waiting for the planner to accept",
    },
    in_planning:              {
        label: "In Planning",
        labels: { doctor: "Planning in Progress", patient: "Planning in Progress" },
        tone: "sky",     group: "planning",
        description: "Planner is actively designing the treatment plan",
    },
    info_requested:           {
        label: "Awaiting Doctor",
        labels: { doctor: "Action Needed From You", patient: "Awaiting Doctor" },
        tone: "amber",   group: "planning",
        description: "Planner has queried the doctor — case on hold pending response",
    },
    submitted_to_qa:          {
        label: "Internal QA",
        // QA is an internal parallel step. From the doctor's & patient's
        // point of view the case is still "Planning in Progress" — they
        // shouldn't see an extra step that doesn't change what they need
        // to do.
        labels: { doctor: "Planning in Progress", patient: "Planning in Progress" },
        tone: "violet",  group: "qa",
        description: "Planner has sent the plan to internal QA for review",
    },
    /* iter101.0 — Self-Plan pathway statuses. Doctor authors the plan
       in the wizard; from their perspective the case is still "in
       planning" whether it's queued for admin review, sitting with QA,
       or being finalised. Kept as distinct DB keys for admin/QA views. */
    plan_authoring:           {
        label: "Self-Plan Authoring",
        labels: { doctor: "Planning in Progress", patient: "Planning in Progress" },
        tone: "sky",     group: "planning",
        description: "Doctor-authored plan is being finalised for submission",
    },
    pending_admin_review:     {
        label: "Pending Admin Review",
        labels: { doctor: "Planning in Progress", patient: "Planning in Progress" },
        tone: "amber",   group: "planning",
        description: "Self-plan submitted — admin needs to route it to QA",
    },
    in_qa_review:             {
        label: "In QA Review",
        labels: { doctor: "Planning in Progress", patient: "Planning in Progress" },
        tone: "violet",  group: "qa",
        description: "Self-plan is with internal QA for review",
    },
    sent_for_approval:        {
        label: "Awaiting Doctor Approval",
        labels: { doctor: "Plan Ready for Review", patient: "Awaiting Doctor Approval" },
        tone: "sky",     group: "doctor_review",
        description: "Plan is with the doctor for review and approval",
    },
    ready_for_review:         {
        label: "Awaiting Doctor Approval",
        labels: { doctor: "Plan Ready for Review", patient: "Awaiting Doctor Approval" },
        tone: "sky",     group: "doctor_review",
        description: "Plan is with the doctor for review and approval",
    },
    revision_requested:       {
        label: "Revision Requested",
        labels: { doctor: "Revision in Progress", patient: "Revision in Progress" },
        tone: "rose",    group: "planning",
        description: "Doctor reviewed the plan and asked the planner to revise",
    },
    approved:                 {
        label: "Doctor Approved",
        labels: { doctor: "Approved", patient: "Approved" },
        tone: "emerald", group: "approval_routing",
        description: "Doctor signed off — admin needs to route to production",
    },
    /* iter98.99ch — CoE MFG-spec authoring window. Doctor approved but
       the planner still has the MFG spec to finalise before Admin can
       route the case to a production tech. Doctor + patient see the
       rolled-up "Approved · Preparing Production" label so the back-
       and-forth between planner ↔ admin stays internal. */
    approved_pending_mfg: {
        label: "MFG Spec Pending",
        labels: { doctor: "Approved · Preparing Production", patient: "Approved · Preparing Production" },
        tone: "amber", group: "approval_routing",
        description: "Doctor approved — planner is finalising the manufacturing spec.",
    },
    awaiting_production_assignment: {
        label: "Awaiting Production",
        labels: { doctor: "Approved · Preparing Production", patient: "Approved · Preparing Production" },
        tone: "amber", group: "approval_routing",
        description: "MFG spec finalised — admin assigning a production tech.",
    },
    assigned_to_production:   {
        label: "Assigned to Production",
        labels: { doctor: "In Production", patient: "In Production" },
        tone: "violet",  group: "production",
        description: "Admin assigned the case — production tech can claim it",
    },
    submitted_to_production:  {
        label: "Assigned to Production",
        labels: { doctor: "In Production", patient: "In Production" },
        tone: "violet",  group: "production",
        description: "Routed straight to production (doctor-self-planned case)",
    },
    in_production:            {
        label: "Production Started",
        labels: { doctor: "In Production", patient: "In Production" },
        tone: "violet",  group: "production",
        description: "Production tech accepted the case and is fabricating the aligners",
    },
    shipped:                  { label: "Aligners Shipped",          tone: "sky",     group: "production",        description: "Aligners shipped to the doctor — awaiting confirmation of receipt" },
    in_treatment:             { label: "Aligners Received",         tone: "amber",   group: "treatment",         description: "Aligners arrived at the clinic — awaiting fitting appointment" },
    treatment_started:        { label: "Treatment Started",         tone: "emerald", group: "treatment",         description: "Doctor logged the fitment — patient is wearing aligners" },
    refinement_requested:     {
        label: "Refinement Requested",
        labels: { doctor: "Refinement in Progress", patient: "Refinement in Progress" },
        tone: "amber",   group: "treatment",
        description: "Doctor requested a refinement mid-treatment",
    },
    completed:                { label: "Completed",                 tone: "emerald", group: "terminal",          description: "Treatment finished, retainers fitted" },
    cancelled:                { label: "Cancelled",                 tone: "rose",    group: "terminal",          description: "Case cancelled" },
};

export const STATUS_GROUPS = [
    { id: "intake",            label: "Intake",                    statuses: ["draft", "scan_visit_scheduled", "scan_visit_completed", "submitted"] },
    { id: "planning",          label: "Planning",                  statuses: ["assigned_to_planner", "in_planning", "info_requested", "revision_requested", "plan_authoring", "pending_admin_review"] },
    { id: "qa",                label: "Internal QA",               statuses: ["submitted_to_qa", "in_qa_review"] },
    { id: "doctor_review",     label: "Doctor Review",             statuses: ["sent_for_approval", "ready_for_review"] },
    { id: "approval_routing",  label: "Awaiting Assignment",       statuses: ["approved", "approved_pending_mfg", "awaiting_production_assignment"] },
    { id: "production",        label: "Production",                statuses: ["assigned_to_production", "submitted_to_production", "in_production", "shipped"] },
    { id: "treatment",         label: "In Treatment",              statuses: ["in_treatment", "treatment_started", "refinement_requested"] },
    { id: "terminal",          label: "Closed",                    statuses: ["completed", "cancelled"] },
];

export const TONE_CLASSES = {
    slate:    "bg-slate-100 text-slate-700 border-slate-200",
    amber:    "bg-amber-50 text-amber-800 border-amber-200",
    violet:   "bg-violet-50 text-violet-800 border-violet-200",
    rose:     "bg-rose-50 text-rose-700 border-rose-200",
    emerald:  "bg-emerald-50 text-emerald-800 border-emerald-200",
    sky:      "bg-sky-50 text-sky-800 border-sky-200",
};

/**
 * Resolve the display label for a status, with optional portal-specific
 * overrides. Falls back to the default label, then to the status key itself.
 *
 * Supported portals: "admin" (default), "doctor", "patient", "coe".
 */
export function statusLabel(status, portal) {
    const cfg = STATUS_CONFIG[status];
    if (!cfg) return (status || "").replace(/_/g, " ");
    if (portal && cfg.labels && cfg.labels[portal]) return cfg.labels[portal];
    return cfg.label;
}

export function statusTone(status) {
    return STATUS_CONFIG[status]?.tone || "slate";
}

export function statusToneClass(status) {
    return TONE_CLASSES[statusTone(status)];
}

export function statusGroup(status) {
    return STATUS_CONFIG[status]?.group || "intake";
}

/* iter101.5 — Canonical status buckets so every surface (doctor
   dashboard, admin treatment tab, case row, planner queue) agrees
   on WHEN a plan is considered "approved by the doctor".

   `POST_APPROVAL_STATUSES`  — Doctor has signed off. The plan is
       locked to production even though follow-up work still remains
       (MFG spec, admin routing, actual manufacture, shipment,
       fitment, monitoring). Any UI that renders "Plan not yet
       approved" copy MUST check this set first.

   `POST_APPROVAL_PRE_PRODUCTION` — Doctor approved but the case
       hasn't started manufacturing yet. This is where the planner's
       MFG-spec authoring and admin's production routing happen.
       Used by the planner dashboard's "MFG spec pending" tile.

   `ACTIVE_TREATMENT_STATUSES` — Case is moving through fabrication /
       shipment / fitment. Used by the doctor's cases-pipeline
       "Active Treatment" bucket + Dashboard "In treatment" KPI.

   `WITH_INVISIQUE_STATUSES` — Case is on Invisique's side of the
       ledger — the doctor has done everything they can and is now
       waiting on us. Powers the doctor dashboard's "With Invisique
       team" KPI so the number matches what the doctor actually
       expects to see when they open the pipeline.
*/
export const POST_APPROVAL_STATUSES = new Set([
    "approved",
    "approved_pending_mfg",
    "awaiting_production_assignment",
    "assigned_to_production",
    "submitted_to_production",
    "in_production",
    "shipped",
    "in_treatment",
    "treatment_started",
    "refinement_requested",
    "completed",
]);

export const POST_APPROVAL_PRE_PRODUCTION = new Set([
    "approved",
    "approved_pending_mfg",
    "awaiting_production_assignment",
]);

export const ACTIVE_TREATMENT_STATUSES = new Set([
    "approved",
    "approved_pending_mfg",
    "awaiting_production_assignment",
    "assigned_to_production",
    "submitted_to_production",
    "in_production",
    "shipped",
    "in_treatment",
    "treatment_started",
    "refinement_requested",
]);

export const WITH_INVISIQUE_STATUSES = new Set([
    "submitted",
    "assigned_to_planner",
    "in_planning",
    "submitted_to_qa",
    "plan_authoring",
    "pending_admin_review",
    "in_qa_review",
    "approved_pending_mfg",
    "awaiting_production_assignment",
    "assigned_to_production",
    "submitted_to_production",
    "in_production",
    "shipped",
]);

export const AWAITING_DOCTOR_STATUSES = new Set([
    "info_requested",
    "sent_for_approval",
    "ready_for_review",
    "scan_visit_completed",
    "in_treatment",
]);

export function isPostApproval(status) {
    return POST_APPROVAL_STATUSES.has(status);
}
export function isActiveTreatment(status) {
    return ACTIVE_TREATMENT_STATUSES.has(status);
}
export function isWithInvisique(status) {
    return WITH_INVISIQUE_STATUSES.has(status);
}
export function isAwaitingDoctor(status) {
    return AWAITING_DOCTOR_STATUSES.has(status);
}

export function statusDescription(status) {
    return STATUS_CONFIG[status]?.description || "";
}

/**
 * When `status === "in_production"`, the production tech / admin Production
 * portals want to see the *current micro-stage* (Digital Prep / 3D Printing /
 * Thermoforming / …) rather than the generic "Production Started" pill.
 *
 * Doctors and patients should NOT see the granular label — they only care
 * about the high-level state. Hence `productionStage` is an opt-in prop on
 * `<CaseStatusBadge>`; portals that want the granular label pass it, the
 * rest ignore it.
 *
 * Centralising the map here so the label set stays in sync with the
 * backend's `PRODUCTION_STAGES` in `production_workflow.py`.
 */
export const PRODUCTION_STAGE_LABELS = {
    assigned:           "Awaiting Acceptance",
    digital_prep:       "Digital Prep",
    model_production:   "3D Printing",
    thermoforming:      "Thermoforming",
    trimming_finishing: "Trimming & Finishing",
    cleaning:           "Cleaning & Sterilization",
    qc_review:          "QC Review",
    packaging:          "Packaging",
    shipped:            "Shipping",
    closed:             "Closed",
};

export function productionStageLabel(stage) {
    if (!stage) return "";
    return PRODUCTION_STAGE_LABELS[stage] || stage.replace(/_/g, " ");
}
